import * as designSystem from '@parrot-software/design-system';
import * as designSystemIcons from '@parrot-software/design-system/icons';
import * as hooks from '@parrot-software/hooks';
import * as observability from '@parrot-software/observability';
import * as tokens from '@parrot-software/tokens';
import * as utils from '@parrot-software/utils';
import React from 'react';
import ReactDOM from 'react-dom';
import * as jsxRuntime from 'react/jsx-runtime';

// Must match package.json. Importing package.json here is impossible (tsconfig rootDir: src blocks
// the root file, and the packages' exports maps block theirs), so a unit test enforces the sync:
// bumping a toolkit package without updating this map fails. React needs no equivalent because
// React.version is runtime truth.
export const TOOLKIT_SHARED_VERSIONS = {
  '@parrot-software/design-system': '2.2.0',
  '@parrot-software/hooks': '1.1.0',
  '@parrot-software/observability': '1.3.0',
  '@parrot-software/tokens': '1.0.5',
  '@parrot-software/utils': '1.1.0',
} as const;

export type ToolkitPackage = keyof typeof TOOLKIT_SHARED_VERSIONS;

const TOOLKIT_MODULES: Record<ToolkitPackage, unknown> = {
  '@parrot-software/design-system': designSystem,
  '@parrot-software/hooks': hooks,
  '@parrot-software/observability': observability,
  '@parrot-software/tokens': tokens,
  '@parrot-software/utils': utils,
};

/**
 * Toolkit subpaths shared on their own, each tied to the package it ships in. A shared key only
 * matches its exact specifier, so without this entry every remote bundled its own copy of the icons.
 */
export const TOOLKIT_SHARED_SUBPATHS = {
  '@parrot-software/design-system/icons': '@parrot-software/design-system',
} as const;

export type ToolkitSubpath = keyof typeof TOOLKIT_SHARED_SUBPATHS;

const TOOLKIT_SUBPATH_MODULES: Record<ToolkitSubpath, unknown> = {
  '@parrot-software/design-system/icons': designSystemIcons,
};

const subpathVersion = (subpath: ToolkitSubpath): string => TOOLKIT_SHARED_VERSIONS[TOOLKIT_SHARED_SUBPATHS[subpath]];

/** The version each shared toolkit specifier is provided at, subpaths included. */
export const TOOLKIT_SHARED_SPECIFIER_VERSIONS: Record<string, string> = {
  ...TOOLKIT_SHARED_VERSIONS,
  ...Object.fromEntries(
    (Object.keys(TOOLKIT_SHARED_SUBPATHS) as ToolkitSubpath[]).map((subpath) => [subpath, subpathVersion(subpath)]),
  ),
};

const reactSingleton = (lib: unknown) => ({
  lib: () => lib,
  scope: 'default',
  shareConfig: { requiredVersion: '^18.0.0', singleton: true },
  version: React.version,
});

const toolkitSingleton = (lib: unknown, version: string) => ({
  lib: () => lib,
  scope: 'default',
  shareConfig: { requiredVersion: `^${version}`, singleton: true },
  version,
});

const toolkitShared = Object.fromEntries(
  (Object.keys(TOOLKIT_SHARED_VERSIONS) as ToolkitPackage[]).map((pkg) => [
    pkg,
    toolkitSingleton(TOOLKIT_MODULES[pkg], TOOLKIT_SHARED_VERSIONS[pkg]),
  ]),
);

const toolkitSubpathShared = Object.fromEntries(
  (Object.keys(TOOLKIT_SHARED_SUBPATHS) as ToolkitSubpath[]).map((subpath) => [
    subpath,
    toolkitSingleton(TOOLKIT_SUBPATH_MODULES[subpath], subpathVersion(subpath)),
  ]),
);

/**
 * Modules the shell provides to every remote microfrontend, all as singletons.
 *
 * Every toolkit package holds page-wide state (the initialized telemetry SDK, the design system's
 * toaster and portals), so the remote must reuse the host's already loaded instance instead of
 * negotiating its own copy. Router, i18n and axios are not shared on purpose: the remote uses
 * incompatible majors and owns its instances.
 */
export const hostSharedDependencies = {
  react: reactSingleton(React),
  'react-dom': reactSingleton(ReactDOM),
  'react/jsx-runtime': reactSingleton(jsxRuntime),
  ...toolkitShared,
  ...toolkitSubpathShared,
};
